import { motion, AnimatePresence } from 'framer-motion';
import React from 'react';

interface PatientNotFoundModalProps {
  isOpen: boolean;
  onContinue: () => void;
}

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2,
    },
  },
};

const overlayVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

export const PatientNotFoundModal: React.FC<PatientNotFoundModalProps> = ({
  isOpen,
  onContinue,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
          {/* Overlay */}
          <motion.div
            className='absolute inset-0 bg-black/50'
            variants={overlayVariants}
            initial='hidden'
            animate='visible'
            exit='exit'
            onClick={onContinue}
          />

          {/* Modal */}
          <motion.div
            className='relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 md:p-8 z-10'
            variants={modalVariants}
            initial='hidden'
            animate='visible'
            exit='exit'
          >
            {/* Close button */}
            <button
              onClick={onContinue}
              className='absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors'
              aria-label='Close modal'
            >
              <svg
                className='w-6 h-6'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>

            {/* Content */}
            <div className='text-center'>
              {/* Icon */}
              <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4' style={{ backgroundColor: 'var(--clinic-primary)', opacity: 0.1 }}>
                <svg
                  className='h-8 w-8'
                  style={{ color: 'var(--clinic-primary)' }}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                  />
                </svg>
              </div>

              {/* Title */}
              <h3
                className='text-2xl font-semibold mb-3'
                style={{ color: 'var(--clinic-text)' }}
              >
                Patient Not Found
              </h3>

              {/* Message */}
              <p
                className='text-base mb-6'
                style={{ color: 'var(--clinic-text-secondary)' }}
              >
                We couldn't find a patient record matching the information you provided. You can continue with the new patient flow using the information you've entered.
              </p>

              {/* Continue Button */}
              <motion.button
                onClick={onContinue}
                className='h-[54px] w-full px-6 py-3 rounded-xl text-base font-semibold text-white transition-all duration-300 hover:opacity-90'
                style={{ backgroundColor: 'var(--clinic-primary)' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Continue
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

